import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }


  #handleCartError = (event) => {
    const message = event.detail?.data?.message || '';
    if (!message.includes('Safai Club releases are available only')) return;
    let dialog = document.getElementById('safai-club-access-dialog');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'safai-club-access-dialog';
      dialog.setAttribute('aria-labelledby', 'safai-club-access-title');
      dialog.style.cssText = 'max-width:480px;width:calc(100% - 32px);padding:32px;border:1px solid #b9a878;background:#fff;color:#181818;border-radius:8px;';
      dialog.innerHTML = '<h2 id="safai-club-access-title">Your Safai Club access</h2><p>This release is reserved for Safai Club members.</p><p>Already a member? Sign in with the email you used to join. We’ll bring you back to this release.</p><p><a data-safai-login class="button" style="display:block;text-align:center;margin:16px 0">Member sign in</a></p><p>Just joined? Use the email on your membership receipt. Access can take a moment to update; sign in and try again. You do not need to buy another membership.</p><p>New to the Club? Buy this bottle and membership together. The bottle is a one-time purchase; membership is $60/month, renewing until canceled. Taxes calculated at checkout.</p><p><button type="button" data-safai-combined class="button" style="width:100%">Join Club &amp; add bottle</button></p><p><a data-safai-join href="/products/safai-club" class="button button--secondary" style="display:block;text-align:center;margin:16px 0">Explore Safai Club</a></p><form method="dialog"><button class="button button--secondary" style="width:100%">Back to the release</button></form>';
      document.body.appendChild(dialog);
    }
    const source = event.target?.closest?.('product-form-component') || document.getElementById(event.detail?.sourceId);
    const selectedVariant = source?.querySelector?.('input[name="id"]')?.value || new URLSearchParams(window.location.search).get('variant');
    const returnTo = source?.dataset?.productUrl || window.location.pathname + window.location.search;
    const combinedButton = dialog.querySelector('[data-safai-combined]');
    if (combinedButton) {
      combinedButton.hidden = !selectedVariant;
      if (selectedVariant) combinedButton.dataset.variant = selectedVariant;
    }
    dialog.querySelector('[data-safai-login]').href = '/customer_authentication/login?return_to=' + encodeURIComponent(returnTo);
    dialog.querySelector('[data-safai-join]').href = '/products/safai-club?safai_return=' + encodeURIComponent(safaiReleasePath(returnTo));
    if (!dialog.open) dialog.showModal();
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    // Failed additions must leave the error visible instead of opening an empty cart.
    if (event.detail?.data?.didError) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Keep a shopper's release available after membership checkout leaves the theme.
// This stores only a product path, never customer or payment information.
const SAFAI_RELEASE = '/products/safai-chocolate-malted-bourbon-barrel-2';
const SAFAI_RETURN_KEY = 'safai-club-return-v1';
function safaiReleasePath(value) {
  if (typeof value !== 'string' || !/^\/products\/[a-z0-9-]+(?:\?variant=\d+)?$/.test(value)) return SAFAI_RELEASE;
  return value.split('?')[0] === '/products/safai-club' ? SAFAI_RELEASE : value;
}

function showSafaiReturnPath() {
  const main = document.querySelector?.('#MainContent');
  if (!main || document.getElementById('safai-club-return')) return;
  const location = window.location;
  const joining = location.pathname === '/products/safai-club';
  // Purchase guidance belongs with product controls, never on the homepage.
  if (!joining) return;
  let saved;
  try {
    const candidate = JSON.parse(window.sessionStorage.getItem(SAFAI_RETURN_KEY) || 'null');
    if (candidate && Date.now() - candidate.at < 86400000 && candidate.at <= Date.now()) saved = candidate;
    else window.sessionStorage.removeItem(SAFAI_RETURN_KEY);
  } catch { /* Storage may be blocked; visible links still work. */ }
  const requested = joining ? new URLSearchParams(location.search).get('safai_return') : null;
  const release = safaiReleasePath(requested || saved?.path || SAFAI_RELEASE);
  if (joining) {
    try { window.sessionStorage.setItem(SAFAI_RETURN_KEY, JSON.stringify({path: release, at: Date.now()})); } catch { /* Optional continuity only. */ }
  }
  const atRelease = location.pathname === release.split('?')[0];
  if (!joining && (atRelease || (!saved && location.pathname !== '/'))) return;
  const panel = document.createElement('section');
  panel.id = 'safai-club-return';
  panel.setAttribute('aria-labelledby', 'safai-club-return-heading');
  panel.style.cssText = 'box-sizing:border-box;width:calc(100% - 32px);max-width:960px;margin:24px auto;padding:24px;border:1px solid #b9a878;border-radius:8px;background:#faf8f2;color:#181818;';
  const heading = document.createElement('h2');
  heading.id = 'safai-club-return-heading';
  heading.textContent = joining ? 'Your Safai Club membership' : saved ? 'Continue to your Safai Club release' : 'Explore Safai Club';
  heading.style.cssText = 'margin:0 0 12px;font-size:24px;';
  const copy = document.createElement('p');
  copy.textContent = joining
    ? 'Already a member? Sign in below instead of purchasing again. Buying a bottle too? Return to the release and choose Join Club & add bottle for one checkout. For membership only, continue below and check the email shown at checkout.'
    : saved
      ? 'Finished joining? Return to the bottle and add it to your cart. If asked to sign in, use the email on your membership receipt. Access can take a moment to update; you do not need to purchase membership again.'
      : 'Discover the Club’s member-only releases. Existing members can sign in with the email they used to join.';
  const links = document.createElement('div');
  links.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;margin-top:16px;';
  const addLink = (text, href, secondary = false) => {
    const link = document.createElement('a');
    link.textContent = text;
    link.href = href;
    link.className = secondary ? 'button button--secondary' : 'button';
    links.appendChild(link);
  };
  addLink(joining || saved ? 'Return to your release' : 'Shop the Club release', release);
  addLink('Member sign in', '/customer_authentication/login?return_to=' + encodeURIComponent(release), true);
  if (!joining && !saved) addLink('Explore membership', '/products/safai-club?safai_return=' + encodeURIComponent(release), true);
  panel.append(heading, copy, links);
  const controls = main.querySelector('product-form-component');
  if (controls) {
    panel.style.cssText = 'margin-block:var(--margin-lg);padding-block:var(--padding-md);border-block-start:1px solid var(--color-border);';
    controls.after(panel);
  }
}
showSafaiReturnPath();
